import { useState, useEffect } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";

const ResetPassword = () => {
  usePageSeo({ path: "/reset-password", title: "Reset Password | Salesby", description: "Reset the password for your Salesby account.", noindex: true });

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { accessToken, redirectAfterReset } = location.state || {};

  useEffect(() => {
    if (!accessToken) {
      console.log('No access token found, redirecting to login');
      toast({
        title: "Error",
        description: "Invalid or expired reset link. Please request a new one.",
        variant: "destructive",
      });
      navigate("/login");
    }
  }, [accessToken, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your password has been updated. Please log in with your new password.",
      });

      // Sign out the user after password reset
      try {
        window.localStorage.setItem('salesby:logout-intent-at', Date.now().toString());
        window.sessionStorage.setItem('salesby:logout-intent-at', Date.now().toString());
      } catch {}
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Reset Your Password</h1>
            <p className="text-muted-foreground mt-2">
              Please enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium">
                New Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full"
                placeholder="Enter your new password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;